import { ProfileMetadata, VideoData } from '../interfaces'
import firebase from 'firebase/compat/app'
import { getShortenedAddress } from '../utils/address'
import { FbEnrichedUser } from '../interfaces/User'
import { db } from './FirebaseUtils'
import { ActivityData, NotificationData } from '../interfaces/Notifications'

interface VideoReaction {
  id: string
  activity_id: string
}

export const fbEnrichUsersFromIds = async (userIds: string[]): Promise<FbEnrichedUser[]> => {
  if (!userIds.length) {
    return []
  }

  const usersData = await db.users
    .where(firebase.firestore.FieldPath.documentId(), 'in', userIds)
    .get()

  const idToUserMap: { [key: string]: any } = {}

  usersData.docs.forEach((userSnapshot) => {
    const user = userSnapshot.data()
    idToUserMap[userSnapshot.id] = {
      address: userSnapshot.id,
      profileImageUri: user.profile_image_uri || undefined,
      displayName: user.display_name || getShortenedAddress(userSnapshot.id),
    }
  })

  return userIds.map(
    (userId) =>
      idToUserMap[userId] || {
        address: userId,
        displayName: getShortenedAddress(userId),
      }
  )
}

const getIdToTranscodingMap = async (
  videosData: firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>
): Promise<{ [key: string]: any }> => {
  const idToTranscodingMap: { [key: string]: any } = {}
  const videoUuids: string[] = []

  videosData.docs.forEach((videoSnapshot) => {
    const video = videoSnapshot.data()
    if (video.video_uuid) {
      videoUuids.push(video.video_uuid)
    }
  })

  const transcodingData = await db.transcoding
    .where(firebase.firestore.FieldPath.documentId(), 'in', videoUuids)
    .get()

  transcodingData.docs.forEach((transcodingSnapshot) => {
    const transcoding = transcodingSnapshot.data()
    idToTranscodingMap[transcodingSnapshot.id] = {
      thumbnail_uri: transcoding.thumbnail_uri,
      video_uri: transcoding.video_uri,
    }
  })

  return idToTranscodingMap
}

const getVideoDataFromSnapshot = (
  videoSnapshot:
    | firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>
    | firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>,
  transcoding: any | undefined
): Omit<VideoData, 'id'> | {} => {
  const video = videoSnapshot.data()

  if (!video) {
    return {}
  }

  return {
    address: videoSnapshot.id || '',
    created_at: video.created_at, //new Date(video.created_at * 1000),
    created_by: video.created_by,
    description: video.description,
    title: video.title,
    audio: video.audio,
    height: video.height,
    width: video.width,
    activity_id: video.activity_id,
    minted: video.minted,
    price: video.price,
    max_editions: video.max_editions,

    thumbnail_uri: transcoding?.thumbnail_uri,
    video_uri: transcoding?.video_uri,
    mp4_uri: transcoding?.mp4_uri,
    trailer_uri: transcoding?.trailer_uri,
    gated: transcoding?.gated,
    duration: transcoding?.duration,
    preview_duration: transcoding?.preview_duration,
    video_transcoding_status: transcoding?.video_transcoding_status,
  }
}

export const fbEnrichVideosFromReactions = async (
  reactions: VideoReaction[]
): Promise<VideoData[]> => {
  if (!reactions.length) {
    return []
  }

  const activityIds = reactions.map((reaction) => reaction.activity_id)

  const videosData = await db.videos.where('activity_id', 'in', activityIds).get()
  const idToVideoMap: { [key: string]: any } = {}
  const idToTranscodingMap: { [key: string]: any } = await getIdToTranscodingMap(videosData)

  videosData.docs.forEach((videoSnapshot) => {
    const video = videoSnapshot.data()
    const transcoding = idToTranscodingMap[video.video_uuid]
    idToVideoMap[video.activity_id] = {
      id: reactions.find((reaction) => reaction.activity_id === video.activity_id)?.id,
      ...getVideoDataFromSnapshot(videoSnapshot, transcoding),
    }
  })

  return activityIds.map(
    (activityId) =>
      idToVideoMap[activityId] || {
        activity_id: activityId,
      }
  )
}

export const fbEnrichVideoFromId = async (videoId: string): Promise<VideoData | null> => {
  const videoSnapshot = await db.videos.doc(videoId).get()
  const video = videoSnapshot.data()

  if (video?.video_uuid) {
    const transcoding = (await db.transcoding.doc(video.video_uuid).get()).data()

    return {
      id: '',
      ...getVideoDataFromSnapshot(videoSnapshot, transcoding),
    } as VideoData
  } else {
    return null
  }
}

export const fbEnrichVideosFromIds = async (ids: string[]): Promise<VideoData[]> => {
  if (!ids.length) {
    return []
  }

  const videosData = await db.videos
    .where(firebase.firestore.FieldPath.documentId(), 'in', ids)
    .get()
  const idToVideoMap: { [key: string]: any } = {}
  const idToTranscodingMap: { [key: string]: any } = {}
  const videoUuids: string[] = []

  videosData.docs.forEach((videoSnapshot) => {
    const video = videoSnapshot.data()
    if (video.video_uuid) {
      videoUuids.push(video.video_uuid)
    }
  })

  if (videoUuids.length) {
    const transcodingData = await db.transcoding
      .where(firebase.firestore.FieldPath.documentId(), 'in', videoUuids)
      .get()

    transcodingData.docs.forEach((transcodingSnapshot) => {
      const transcoding = transcodingSnapshot.data()
      idToTranscodingMap[transcodingSnapshot.id] = {
        thumbnail_uri: transcoding.thumbnail_uri,
        video_uri: transcoding.video_uri,
        duration: transcoding.duration,
      }
    })
  }

  videosData.docs.forEach((videoSnapshot) => {
    const video = videoSnapshot.data()
    const transcoding = idToTranscodingMap[video.video_uuid]
    idToVideoMap[videoSnapshot.id] = {
      id: videoSnapshot.id,
      ...getVideoDataFromSnapshot(videoSnapshot, transcoding),
    }
  })

  return ids.filter((id) => idToVideoMap[id]).map((id) => idToVideoMap[id])
}

export const fbEnrichNotificationsFromActivity = async (
  activity: ActivityData[]
): Promise<NotificationData[]> => {
  if (!activity?.length) {
    return []
  }

  const getProfile = (activity: ActivityData): string => {
    if (activity.verb === 'sold_secondary' || activity.verb === 'sold_primary') {
      return activity.extra.buyer
    } else if (activity.verb === 'bought_secondary' || activity.verb === 'bought_primary') {
      return activity.extra.seller
    } else {
      return activity.actor
    }
  }

  const users = await fbEnrichUsersFromIds(activity.map((activity) => getProfile(activity)))
  const activityIds = activity.map((a) => a.activity_id)
  const videosData = await db.videos.where('activity_id', 'in', activityIds).get()
  const idToAddressMap: { [key: string]: string } = {}

  videosData.docs.forEach((videoSnapshot) => {
    const video = videoSnapshot.data()
    idToAddressMap[video.activity_id] = videoSnapshot.id
  })

  return activity.map((a) => {
    const video = idToAddressMap[a.activity_id]
    const user = users.find((u) => u.address === getProfile(a))
    return {
      user: user,
      address: video,
      activity: a,
    }
  })
}

export const fbGetUserMetadata = async (address: string): Promise<ProfileMetadata | undefined> => {
  const userDoc = (await db.users.doc(address).get()).data()
  let metadata: ProfileMetadata | undefined
  if (userDoc) {
    metadata = {
      display_name: userDoc['display_name'],
      bio: userDoc['bio'],
      profile_image_uri: userDoc['profile_image_uri'],
      banner_image_uri: userDoc['banner_image_uri'],
      twitter_link: userDoc['twitter_link'],
      youtube_link: userDoc['youtube_link'],
      instagram_link: userDoc['instagram_link'],
    }
  }

  return metadata
}
